import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Link,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import type { ApiKeySummary } from "api/ApiKeySettings";
import { FOCUS_RING } from "utils/chackra/controlStyles";
import Panel from "./Panel";

const PLANS_PATH = "/pricing";

/** `date_end` arrives as an ISO or a MySQL `YYYY-MM-DD hh:mm:ss` string. */
const parseDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  return isNaN(date.getTime()) ? null : date;
};

interface ApiKeySummaryPanelProps {
  summary: ApiKeySummary | null;
  loading: boolean;
  error: any;
}

/**
 * Which account, plan and quota the key draws from (spec
 * 2026-10-20-website-api-settings, US3). With 0 requests left it says what a
 * check returns and links to the plans.
 */
const ApiKeySummaryPanel = ({ summary, loading, error }: ApiKeySummaryPanelProps) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);

  let body: JSX.Element;
  if (error) {
    // 409: the key is attached to several accounts (until spec Stage 7).
    const shared = error?.response?.status === 409;
    body = (
      <Alert
        status={shared ? "info" : "error"}
        variant="left-accent"
        bg={shared ? "status.infoSubtle" : "status.errorSubtle"}
        borderColor={shared ? "status.info" : "status.error"}
        color="text.primary"
        borderRadius="md"
        data-testid="api-key-summary-error"
      >
        <AlertIcon color={shared ? "status.info" : "status.error"} />
        <AlertDescription fontSize="sm">
          {t(shared ? "profileApi.summary.shared" : "profileApi.summary.error")}
        </AlertDescription>
      </Alert>
    );
  } else if (loading || !summary) {
    body = (
      <Text role="status" color="text.secondary">
        {t("profileApi.loading")}
      </Text>
    );
  } else {
    const { account, plan, requests_left } = summary;
    const requestsLeft = Number(requests_left) || 0;
    const endDate = parseDate(plan?.date_end ?? null);
    const identity = account.identity ? ` · ${account.identity}` : "";
    body = (
      <>
        <SimpleGrid
          as="dl"
          columns={{ base: 1, md: 3 }}
          spacing={4}
          data-testid="api-key-summary"
        >
          <Box>
            <Text as="dt" fontSize="sm" color="text.secondary">
              {t("profileApi.summary.account")}
            </Text>
            <Text as="dd" color="text.primary" wordBreak="break-word">
              {t(`profileApi.summary.accountType.${account.type}`)}
              {identity}
            </Text>
          </Box>
          <Box>
            <Text as="dt" fontSize="sm" color="text.secondary">
              {t("profileApi.summary.plan")}
            </Text>
            <Text as="dd" color="text.primary">
              {plan?.name || t("profileApi.summary.noPlan")}
              {plan?.name && (endDate || plan.date_end)
                ? ` · ${t("profileApi.summary.until", {
                    date: endDate
                      ? intl.formatDate(endDate, { dateStyle: "medium" })
                      : plan.date_end,
                  })}`
                : ""}
            </Text>
          </Box>
          <Box>
            <Text as="dt" fontSize="sm" color="text.secondary">
              {t("profileApi.summary.requestsLeft")}
            </Text>
            <Text as="dd" color="text.primary" fontFamily="mono">
              {intl.formatNumber(requestsLeft)}
            </Text>
          </Box>
        </SimpleGrid>
        {requestsLeft <= 0 && (
          <Alert
            status="warning"
            variant="left-accent"
            bg="status.warningSubtle"
            borderColor="status.warning"
            color="text.primary"
            borderRadius="md"
            mt={4}
            data-testid="api-key-zero-requests"
          >
            <AlertIcon color="status.warning" />
            <AlertDescription fontSize="sm">
              {t("profileApi.summary.zeroNotice")}{" "}
              <Link
                as={RouterLink}
                to={PLANS_PATH}
                color="brand.solid"
                textDecoration="underline"
                _focusVisible={FOCUS_RING}
              >
                {t("profileApi.summary.plansLink")}
              </Link>
            </AlertDescription>
          </Alert>
        )}
      </>
    );
  }

  return (
    <Panel id="api-key-summary" title={t("profileApi.summary.heading")}>
      {body}
    </Panel>
  );
};

export default ApiKeySummaryPanel;
